import { useFormikContext } from 'formik';

import { Alert, AlertDescription } from '@src/design-system/alert';
import { Switch } from '@src/design-system/switch';
import { REMINDER_TYPES } from '@src/shared/types/reminder';
import { OtherRuleRepetitionFields } from '@src/shared/features/reminders/dialogs/create-or-edit-reminder/other-rule-repetition-fields';

import type { TCreateReminderFormValues } from '../schema';
import { InfoCircle } from 'iconsax-react';

export function StageRepetitionSection() {
  const { values, setFieldValue } =
    useFormikContext<TCreateReminderFormValues>();

  if (values.type !== REMINDER_TYPES.otherRules) return null;

  const frequency = values.frequency || 0;
  const frequencyCount = values.frequency_count || 0;
  const shouldShowAlert =
    !values.repeatSending ||
    Number(frequency) > 0 ||
    Number(frequencyCount) > 0;

  const handleRepeatSendingChange = (checked: boolean) => {
    setFieldValue('repeatSending', checked);

    if (!checked) {
      setFieldValue('frequency', '');
      setFieldValue('frequency_count', 0);
    }
  };

  return (
    <section className='rounded-xl border border-[#E8E7F0] bg-white p-4'>
      <p className='mb-4 text-sm font-semibold text-gray-500'>4. Repetition</p>

      <div className='flex flex-col gap-4'>
        <div
          className='flex items-center gap-3'
          role='group'
          aria-label='Repetition mode'
        >
          <span
            className={`text-sm font-medium transition-colors ${
              !values.repeatSending ? 'text-gray-900' : 'text-gray-500'
            }`}
          >
            Send Once
          </span>
          <Switch
            checked={Boolean(values.repeatSending)}
            onCheckedChange={handleRepeatSendingChange}
            aria-label={values.repeatSending ? 'Repeat sending' : 'Send once'}
            className='h-7 w-12 shrink-0 data-[state=checked]:bg-primary-1000 data-[state=unchecked]:bg-gray-200 [&>span]:size-5 [&>span]:data-[state=checked]:translate-x-5 [&>span]:data-[state=unchecked]:translate-x-0.5'
          />
          <span
            className={`text-sm font-medium transition-colors ${
              values.repeatSending ? 'text-gray-900' : 'text-gray-500'
            }`}
          >
            Repeat Sending
          </span>
        </div>
        {values.repeatSending ? <OtherRuleRepetitionFields /> : null}

        {shouldShowAlert ? (
          <Alert className='border-0 bg-primary-100 text-primary-1000'>
            <AlertDescription className='text-primary-1000 flex items-center gap-1 text-sm'>
              <span>
                <InfoCircle size={20} />
              </span>
              {values.repeatSending ? (
                <>
                  Send this reminder every{' '}
                  <span className='font-bold'>{frequency}</span> days for a
                  total of <span className='font-bold'>{frequencyCount}</span>{' '}
                  times.
                </>
              ) : (
                <>
                  Send this reminder <span className='font-bold'>Once</span>.
                </>
              )}
            </AlertDescription>
          </Alert>
        ) : null}
      </div>
    </section>
  );
}
